import React, { useState, useEffect } from 'react';
import { Building2, MapPin, CreditCard } from 'lucide-react';
import { InfoPageHeader } from '@/components/brand/InfoPageHeader';

const CompanyInfo = () => {
  // Initialize language from localStorage or default to Latvian
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language') || localStorage.getItem('selectedLanguage');
    return savedLanguage || 'lv';
  });

  // Save language preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const translations = {
    en: {
      title: "Company Information",
      subtitle: "Legal and Banking Details",
      companyDetails: "Company Details",
      companyName: "Company Name",
      registrationNumber: "Registration Number",
      legalAddress: "Legal Address",
      bankingInformation: "Banking Information",
      bankName: "Bank Name",
      swiftCode: "SWIFT/BIC Code",
      accountNumber: "Account Number (IBAN)",
      management: "Management",
      boardMember: "Board Member",
      contactInquiry: "For business inquiries, please contact us at:",
      backToHome: "Back to Home"
    },
    ru: {
      title: "Информация о компании",
      subtitle: "Юридические и банковские реквизиты",
      companyDetails: "Реквизиты компании",
      companyName: "Название компании",
      registrationNumber: "Регистрационный номер",
      legalAddress: "Юридический адрес",
      bankingInformation: "Банковская информация",
      bankName: "Название банка",
      swiftCode: "SWIFT/BIC код",
      accountNumber: "Номер счета (IBAN)",
      management: "Руководство",
      boardMember: "Член правления",
      contactInquiry: "По деловым вопросам обращайтесь:",
      backToHome: "Вернуться на главную"
    },
    lv: {
      title: "Informācija par uzņēmumu",
      subtitle: "Juridiskie un bankas rekvizīti",
      companyDetails: "Uzņēmuma rekvizīti",
      companyName: "Uzņēmuma nosaukums",
      registrationNumber: "Reģistrācijas numurs",
      legalAddress: "Juridiskā adrese",
      bankingInformation: "Bankas informācija",
      bankName: "Bankas nosaukums",
      swiftCode: "SWIFT/BIC kods",
      accountNumber: "Konta numurs (IBAN)",
      management: "Vadība",
      boardMember: "Valdes loceklis",
      contactInquiry: "Biznesa jautājumiem, lūdzu, sazinieties ar mums:",
      backToHome: "Atpakaļ uz sākumlapu"
    }
  };

  const t = translations[language];

  return (
    <div className="rz-page min-h-screen" data-testid="company-page">
      <InfoPageHeader language={language} setLanguage={setLanguage} title={t.title} subtitle={t.subtitle} />

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border border-[#E7E5E4]">
          
          {/* Company Details */}
          <div className="p-8 border-b border-[#E7E5E4]">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.companyDetails}
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-[#78716C] mb-1">{t.companyName}</p>
                    <p className="text-lg font-medium text-[#1C1917]">
                      SIA „Hermes invest"
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C] mb-1">{t.registrationNumber}</p>
                    <p className="text-lg font-mono text-[#1C1917]">
                      LV42103078050
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Legal Address */}
          <div className="p-8 border-b border-[#E7E5E4]">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-[#C2410C]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.legalAddress}
                </h2>
                <p className="text-lg text-[#1C1917]">
                  Vecā ostmala 54
                </p>
                <p className="text-lg text-[#1C1917]">
                  Liepāja, Latvija, LV-3401
                </p>
              </div>
            </div>
          </div>

          {/* Banking Details */}
          <div className="p-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#F5F2EC] rounded-full flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-[#1C1917]" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-[1.7rem] leading-tight text-[#1C1917] mb-4">
                  {t.bankingInformation}
                </h2>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-[#78716C] mb-1">{t.bankName}</p>
                    <p className="text-lg font-medium text-[#1C1917]">
                      BluOr Bank AS
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C] mb-1">{t.swiftCode}</p>
                    <p className="text-lg font-mono text-[#1C1917]">
                      CBBRLV22
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[#78716C] mb-1">{t.accountNumber}</p>
                    <p className="text-lg font-mono text-[#1C1917] break-all">
                      LV32CBBR1124232600010
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Contact Information */}
        <div className="mt-8 text-center">
          <p className="text-[#57534E] mb-2">
            {t.contactInquiry}
          </p>
          <a 
            href="mailto:rozedinapoliorders@gmail.com" 
            className="text-[#1C1917] hover:text-[#1C1917] font-semibold"
          >
            rozedinapoliorders@gmail.com
          </a>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <a 
            href="/" 
            className="inline-flex items-center text-[#57534E] hover:text-[#1C1917] transition-colors"
          >
            ← {t.backToHome}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CompanyInfo;
